<template>
    <div id="report-new">
        <AppBreadCrumb :paths="[{ title: $t('report.detailReport') }]" :select="true">
            <select id="time-select" v-model="customDateFilter">
                <option value="today">{{ $t("time.today") }}</option>
                <option value="yesterday">{{ $t("time.yesterday") }}</option>
                <option value="current-week">
                    {{ $t("time.currentWeek") }}
                </option>
                <option value="last-week">{{ $t("time.lastWeek") }}</option>
                <option value="current-month">
                    {{ $t("time.currentMonth") }}
                </option>
                <option v-for="(m, i) in getLast11Months()" :key="i" :value="m.date">
                    {{ m.name }}
                </option>
                <option value="custom">
                    {{ $t("time.custom") }}
                </option>
            </select>
        </AppBreadCrumb>

        <div class="report-container">
            <ReportFilterTool v-model="filters" />
            <div class="report-content">
                <ReportMenu
                    :loading="loading"
                    :stats="stats"
                    :update-query-params="updateQueryParams"
                    :current-tab="currentTab"
                />
                <div class="chart-container" ref="chartContainer">
                    <div class="report-page-container">
                        <Overall :filters="filters" v-if="currentTab === ReportTabs.OVERALL" />
                        <Calendar :filters="filters" v-if="currentTab === ReportTabs.Calendar" />
                        <Mail :filters="filters" v-if="currentTab === ReportTabs.Mail" />
                        <ChatDistributions :filters="filters" v-if="currentTab === ReportTabs.CHAT_DISTRIBUTIONS" />
                        <AgentPerformance :filters="filters" v-if="currentTab === ReportTabs.AGENT_PERFORMANCE" />
                        <AgentActivitiesReport :filters="filters" v-if="currentTab === ReportTabs.AGENT_ACTIVITIES" />
                        <AgentWorktime :filters="filters" v-if="currentTab === ReportTabs.AGENT_WORK_TIME" />
                        <CustomerSatisfaction
                            :filters="filters"
                            v-if="currentTab === ReportTabs.CUSTOMER_SATISFACTION"
                        />
                        <WebsiteAnalysisReport :filters="filters" v-if="currentTab === ReportTabs.WEBSITE_ANALYSIS" />
                        <ChannelBreakdown :filters="filters" v-if="currentTab === ReportTabs.CHANNEL_BREAKDOWN" />
                        <WabaAnalys :filters="filters" v-if="currentTab === ReportTabs.WHATSAPP_BUSINESS_API" />
                        <Ticket :filters="filters" v-if="currentTab === ReportTabs.Ticket" />
                        <TaskOverall :filters="filters" v-if="currentTab === ReportTabs.TASK" />
                        <ContactDistributions
                            :filters="filters"
                            v-if="currentTab === ReportTabs.CONTACT_DISTRIBUTIONS"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import AppBreadCrumb from "../../components/common/AppBreadCrumb";
import moment from "moment";
import { REPORT_API } from "../../api/statistics";
import * as echarts from "echarts";
import { createComponent, plugin } from "echarts-for-vue";
import Avatar from "vue-avatar";
import { log2 } from "../../utils/log2";
import { mapActions, mapState } from "vuex";
import AgentActivityBar from "../../components/charts/AgentActivityBar";
import DailyAgentActivities from "../../components/charts/DailyAgentActivities";
import _ from "lodash";
import ChartNotFound from "../Pages/Chatbot/components/ChartNotFound.vue";
import Overall from "./overall/Overall.vue";
import ReportFilterTool from "./common/ReportFilterTool.vue";
import ReportMenu from "./common/ReportMenu.vue";
import ReportTabs from "./common/ReportTabs";
import ChatDistributions from "./chat-distributions/ChatDistributions.vue";
import AgentActivitiesReport from "./agent-activities/AgentActivitiesReport.vue";
import AgentPerformance from "./agent-performance/AgentPerformance.vue";
import AgentWorktime from "./agent-work-time/AgentWorktime.vue";
import CustomerSatisfaction from "./customer-satisfaction/CustomerSatisfaction.vue";
import WebsiteAnalysisReport from "./website-analys/WebsiteAnalysisReport.vue";
import ChannelBreakdown from "./channel-breakdown/ChannelBreakdown.vue";
import TimeUtils from "../../utils/TimeUtils";
import WabaAnalys from "./whatsapp/WabaAnalys.vue";
import Calendar from "./calendar/Calendar.vue";
import Mail from "./mail/Mail.vue";
import Ticket from "./ticket/Ticket.vue";
import TaskOverall from "./task/TaskOverall.vue";
import ContactDistributions from "./contact-distributions-reports/ContactDistributions.vue";
Vue.use(plugin, { echarts });

const defaultStartDate = moment().startOf("day").utc().toISOString();
const defaultEndDate = moment().endOf("day").utc().toISOString();
const log = log2("Reports");

export default {
    name: "Reports",
    components: {
        WabaAnalys,
        ChannelBreakdown,
        WebsiteAnalysisReport,
        CustomerSatisfaction,
        AgentWorktime,
        AgentPerformance,
        AgentActivitiesReport,
        ChatDistributions,
        ReportMenu,
        ReportFilterTool,
        ECharts: createComponent({ echarts }),
        Avatar,
        AppBreadCrumb,
        AgentActivityBar,
        DailyAgentActivities,
        ChartNotFound,
        Overall,
        Calendar,
        Mail,
        Ticket,
        TaskOverall,
        ContactDistributions,
    },
    data() {
        const startDate = this.$route.query.allTime
            ? null
            : this.$route.query.startDate
            ? moment(this.$route.query.startDate).toISOString()
            : defaultStartDate;
        const endDate = this.$route.query.allTime
            ? null
            : this.$route.query.endDate
            ? moment(this.$route.query.endDate).toISOString()
            : defaultEndDate;

        return {
            ReportTabs,
            currentTab: this.$route.query.report || ReportTabs.OVERALL,
            maxDate: moment().utc().toISOString(),
            loading: true,
            calendarTitle: "",
            customDateFilter: this.$route.query.customDate || "today",
            filters: {
                startDate,
                endDate,
                users: this.$route.query.selectedUsers ? this.$route.query.selectedUsers.split(",") : [],
                channels: this.$route.query.channels ? this.$route.query.channels.split(",") : [],
            },
            stats: {
                totalChatCount: 0,
                avgPoint: 0,
                avgResponseTime: 0,
                avgChatDuration: 0,
                avgChatbotDuration: 0,
                avgAgentDuration: 0,
                missedChatCount: 0,
                shortestChatDuration: 0,
                longestChatDuration: 0,
                whatsappChatCount: 0,
            },
        };
    },
    computed: {
        ...mapState({
            language: (state) => state.language.language,
            isDarkMode: (state) => state.user.darkMode,
            channels: (state) => [{ _id: "web", name: "Web" }, ...state.channel.channels],
            users: (state) => state.user.users.data,
        }),
        moment,
        minDate() {
            return this.filters.startDate ? moment().subtract(45, "day").utc().toISOString() : null;
        },
    },
    methods: {
        ...mapActions({
            listChannels: "channel/listChannels",
            setRandomReportCacheKey: "app/setRandomReportCacheKey",
        }),
        fetchStats() {
            log("fetchStats", this.filters);
            this.loading = true;
            REPORT_API.calculateStats(this.filters)
                .then((data) => {
                    this.stats = data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getLast11Months() {
            return TimeUtils.getLast11Months();
        },
        getFormatedDate(filter) {
            log("getFormatedDate", filter);
            let startDate = null;
            let endDate = null;
            if (filter === "today") {
                startDate = moment().startOf("day").utc().toISOString();
                endDate = moment().endOf("day").utc().toISOString();
            } else if (filter === "yesterday") {
                startDate = moment().subtract(1, "days").startOf("day").utc().toISOString();
                endDate = moment().subtract(1, "days").endOf("day").utc().toISOString();
            } else if (filter === "current-week") {
                startDate = moment().startOf("week").startOf("day").utc().toISOString();
                endDate = moment().endOf("week").endOf("day").utc().toISOString();
            } else if (filter === "last-week") {
                startDate = moment().subtract(1, "week").startOf("week").startOf("day").utc().toISOString();
                endDate = moment().subtract(1, "week").endOf("week").endOf("day").utc().toISOString();
            } else if (filter === "current-month") {
                startDate = moment().startOf("month").startOf("day").utc().toISOString();
                endDate = moment().endOf("month").endOf("day").utc().toISOString();
            } else if (filter === "last-month") {
                startDate = moment().subtract(1, "month").startOf("month").startOf("day").utc().toISOString();
                endDate = moment().subtract(1, "month").endOf("month").endOf("day").utc().toISOString();
            } else if (typeof filter === "object") {
                startDate = filter.start;
                endDate = filter.end;
            } else {
                startDate = this.filters.startDate;
                endDate = this.filters.endDate;
            }
            return {
                startDate,
                endDate,
            };
        },
        updateQueryParams(key, value) {
            if (key === "report") {
                this.currentTab = value;
                // if (value === 5) this.renderCalendar();
            }
            if (value === "" || value == null) {
                let query = Object.assign({}, this.$route.query);
                delete query[key];
                this.$router.replace({ query });
            } else {
                let { query } = this.$route;
                this.$router.replace({
                    query: { ...query, [key]: encodeURI(value) },
                });
            }
        },
        getStatistics: _.debounce(async function () {
            this.fetchStats();
        }, 200),
        queryBuilder() {
            const { startDate, endDate, channels, users } = this.filters;
            if (this.customDateFilter !== "custom") {
                const date = this.getFormatedDate(this.customDateFilter);
                if (
                    !moment(date.startDate).isSame(moment(startDate).utc().toISOString()) ||
                    !moment(date.endDate).isSame(moment(endDate).utc().toISOString())
                ) {
                    this.customDateFilter = "custom";
                }
            }
            const queries = {
                report: this.currentTab,
                customDate: this.customDateFilter === "string" ? this.customDateFilter : "custom",
            };
            if (startDate) queries["startDate"] = startDate;
            if (endDate) queries["endDate"] = endDate;
            if (channels?.length > 0) queries["channels"] = channels.join(",");
            if (users?.length > 0) queries["selectedUsers"] = users.join(",");
            this.$router.replace({
                query: {
                    ...queries,
                },
            });
        },
    },
    mounted() {
        this.setRandomReportCacheKey();
        moment.locale(this.$i18n.locale);
        if (this.customDateFilter !== "custom") {
            const { startDate, endDate } = this.getFormatedDate(this.customDateFilter);
            this.filters.startDate = startDate;
            this.filters.endDate = endDate;
        }
        this.getStatistics();
    },

    watch: {
        language(newValue) {
            log("new language", newValue);
            this.getStatistics();
        },
        filters: {
            deep: true,
            handler(newVal, oldVal) {
                this.getStatistics();
                this.queryBuilder();
            },
        },
        customDateFilter(newVal, oldVal) {
            const { startDate, endDate } = this.getFormatedDate(newVal);
            this.filters.startDate = startDate;
            this.filters.endDate = endDate;
        },
        currentTab() {
            this.$nextTick(() => {
                if (this.$refs.chartContainer) {
                    this.$refs.chartContainer.scrollTop = 0;
                }
            });
        },
        isDarkMode(newValue, oldValue) {},
    },
};
</script>

<style lang="scss" scoped>
#report-new {
    overflow: hidden;
}
.report-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    width: 100%;
    height: calc(100vh - 120px);

    .report-content {
        display: flex;
        gap: 10px;
        height: 100%;

        .chart-container {
            width: calc(100vw - 390px);
            max-width: calc(100vw - 390px);
            overflow-y: auto;
            height: 100%;

            &::-webkit-scrollbar {
                width: 4px;
            }
            &::-webkit-scrollbar-track {
                background: transparent;
            }
            &::-webkit-scrollbar-thumb {
                background: rgba(0, 0, 0, 0.1);
                border-radius: 4px;
            }
            &::-webkit-scrollbar-thumb:hover {
                background: rgba(0, 0, 0, 0.18);
            }
            scrollbar-width: thin;
            scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
        }
    }
}

.new-theme {
    #report-new {
        .report-page-container {
            min-height: 500px !important;
            background-color: #ffffff;
            padding: 15px 20px;
            border-radius: 6px;
            box-shadow: 0 0 3px #00000080;
        }

        .report-page-container-overall {
            background-color: #ffffff;
            padding: 15px 20px;
            border-radius: 6px;
            max-width: calc(100vw - 400px);
        }

        #time-select {
            border: none;
            background-color: #f3f6f9;
            padding: 3px 10px;
            border-radius: 6px;
            font-size: 12px;
            font-family: Poppins;
            font-weight: bolder;
            color: #3699ff;
        }

        .md-chip {
            border-radius: 6px;
            font-weight: 600;
            height: 26px;
            line-height: 26px;
        }

        .pageButtons {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 30px;
            height: 30px;
            border-radius: 6px;
            background-color: #ffffff;
            font-size: 22px;
            border: 1px solid #6c6c6c;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
                background-color: #f3f6f9;
            }
        }
    }
}

.dark-mode {
    #report-new {
        .box {
            background-color: #1e1e2d;

            p {
                color: #ffffff !important;
            }
        }

        .box.yes-hover {
            &:hover {
                background-color: #333440;
            }
        }

        .report-page-container {
            background-color: #1e1e2d !important;
            color: #ffffff !important;
            min-height: 500px !important;

            h5 {
                color: #ffffff !important;
            }
        }

        .chart-container {
            &::-webkit-scrollbar-thumb {
                background: rgba(255, 255, 255, 0.08);
            }
            &::-webkit-scrollbar-thumb:hover {
                background: rgba(255, 255, 255, 0.15);
            }
            scrollbar-color: rgba(255, 255, 255, 0.08) transparent;
        }
    }
}
</style>
<style lang="scss">
.dark-mode {
    #report-new {
        .form-control {
            background-color: #1e1e2d !important;
            color: #ffffff !important;
            border: transparent;
        }
    }
}
.report-filter-tool { position: sticky; top: 20px; z-index: 10; }
.report-menu { position: sticky; top: 0; max-height: calc(100vh - 210px); overflow-y: auto; }
</style>